
import { useState, useEffect, useRef } from 'react';

interface UseLazyImageOptions {
  rootMargin?: string;
  threshold?: number;
  placeholderSrc?: string;
}

export const useLazyImage = (
  src: string | null, 
  options: UseLazyImageOptions = {}
) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string | null>(options.placeholderSrc || null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  
  useEffect(() => {
    if (!src) {
      setCurrentSrc(options.placeholderSrc || null);
      return;
    }

    // Reset states when src changes
    if (imgRef.current && imgRef.current.src !== src) {
      setIsLoaded(false);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When element enters viewport
        if (entry.isIntersecting) {
          setInView(true);
          
          // Start loading the full image
          if (src) {
            const img = new Image();
            img.src = src;
            img.onload = () => {
              setIsLoaded(true);
              setCurrentSrc(src);
            };
            img.onerror = () => {
              console.error(`Failed to load image: ${src}`);
              setCurrentSrc(options.placeholderSrc || null);
            };
          }
          
          // Unobserve after it's in view
          if (imgRef.current) {
            observer.unobserve(imgRef.current);
          }
        }
      },
      {
        rootMargin: options.rootMargin || '200px 0px',
        threshold: options.threshold || 0.1
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, [src, options.placeholderSrc, options.rootMargin, options.threshold]);

  return {
    isLoaded,
    inView,
    currentSrc,
    imgRef,
    blur: !isLoaded && inView
  };
};
